import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Loader2, AlertTriangle, CheckCircle2, MapPin, ShieldCheck, ShieldAlert, Maximize2, Crosshair } from "lucide-react";
import { api } from "@/lib/api";
import { Btn, Badge, labelCls, ASelect, ATextarea, useSave } from "../AdminUI";
import MediaPicker from "@/components/admin/MediaPicker";
import NavigateToProperty from "@/components/admin/NavigateToProperty";

const SERVICES = ["House Wash", "Building Exterior Wash", "Commercial Exterior Cleaning", "Pressure Washing",
  "Soft Washing", "Driveway", "Sidewalk", "Concrete", "Roof Wash", "Deck / Patio", "Fence", "Gutters",
  "Parking Lot", "Dumpster Pad", "Custom Service"];

const confColor = (c) => ({ HIGH: "green", MEDIUM: "amber", LOW: "red" }[c] || "gray");
const NAIP = "https://imagery.nationalmap.gov/arcgis/rest/services/USGSNAIPPlus/ImageServer/exportImage";
const naipUrl = (bbox, w = 1100, h = 1100) => `${NAIP}?bbox=${bbox.join(",")}&bboxSR=4326&imageSR=4326&size=${w},${h}&format=png&f=image`;

// Build a square bbox (deg) centred on lat/lon for a given ground context in metres.
const squareBbox = (lat, lon, meters) => {
  const dLat = meters / 111320;
  const dLon = meters / (111320 * Math.cos((lat * Math.PI) / 180) || 1);
  return [lon - dLon, lat - dLat, lon + dLon, lat + dLat];
};
const padBbox = (b, pct) => { const dx = (b[2] - b[0]) * pct, dy = (b[3] - b[1]) * pct; return [b[0] - dx, b[1] - dy, b[2] + dx, b[3] + dy]; };

export default function AIQuoteModal({ customers, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ address: "", property_type: "residential", services: [], description: "", conditions: "", customer_id: "" });
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [ov, setOv] = useState({});
  const [saving, run] = useSave();

  const [verifying, setVerifying] = useState(false);
  const [ver, setVer] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [override, setOverride] = useState(false);
  const [chosenIdx, setChosenIdx] = useState(0);
  const [photoId, setPhotoId] = useState("");
  // Imagery / parcel intel
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [viewMode, setViewMode] = useState("property"); // property | parcel | wider
  const [enlarge, setEnlarge] = useState(false);

  const round2 = (n) => Math.round(n * 100) / 100;
  const toggleSvc = (s) => setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));

  const resetVerification = () => { setVer(null); setConfirmed(false); setOverride(false); setChosenIdx(0); setImgOk(true); setIntel(null); setViewMode("property"); };
  const onAddressChange = (val) => { setF((p) => ({ ...p, address: val })); if (ver || confirmed || override) resetVerification(); };

  const loadIntel = async (lat, lon, state) => {
    setIntelLoading(true); setImgOk(true);
    try { const r = await api.post("/admin/ai-quote/property-intel", { latitude: lat, longitude: lon, state }); setIntel(r.data); }
    catch { setIntel(null); }
    finally { setIntelLoading(false); }
  };

  const verifyAddress = async () => {
    if (!f.address || f.address.trim().length < 4) { toast.error("Enter a complete street address."); return; }
    setVerifying(true); setImgOk(true); setIntel(null);
    try {
      const r = await api.post("/admin/ai-quote/verify-address", { address: f.address });
      setVer(r.data); setConfirmed(false); setOverride(false); setChosenIdx(0);
      if (r.data.status === "verified") loadIntel(r.data.latitude, r.data.longitude, r.data.state);
      else if (r.data.status === "not_found") toast.warning("Address not verified — check spelling or continue manually.");
      else if (r.data.status === "error") toast.error("Verification service unavailable — you can continue manually.");
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setVerifying(false); }
  };

  const useMatch = () => {
    const m = ver?.matches?.[chosenIdx]; if (!m) return;
    setF((p) => ({ ...p, address: m.matched_address }));
    setVer({ ...ver, status: "verified", matched_address: m.matched_address, latitude: m.latitude, longitude: m.longitude, zip: m.zip, city: m.city, state: m.state, match_quality: "single_exact" });
    setChosenIdx(0); loadIntel(m.latitude, m.longitude, m.state);
  };

  const canAnalyze = (ver?.status === "verified" && confirmed) || override;
  const lat = ver?.latitude, lon = ver?.longitude;
  const parcel = intel?.parcel?.found ? intel.parcel : null;
  const imagery = intel?.imagery;

  const currentBbox = () => {
    if (!lat) return null;
    if (viewMode === "wider") return squareBbox(lat, lon, 220);
    if (viewMode === "parcel" && parcel?.bbox) return padBbox(parcel.bbox, 0.2);
    return squareBbox(lat, lon, 70);
  };
  const bbox = currentBbox();
  // parcel ring -> % coords for the current bbox
  const ringPct = () => {
    if (!parcel?.ring || !bbox) return "";
    const [x0, y0, x1, y1] = bbox;
    return parcel.ring.map(([x, y]) => `${((x - x0) / (x1 - x0)) * 100},${((y1 - y) / (y1 - y0)) * 100}`).join(" ");
  };

  const analyze = async () => {
    if (f.services.length === 0) { toast.error("Select at least one service."); return; }
    if (!canAnalyze) { toast.error("Verify & confirm the property, or continue unverified."); return; }
    setAnalyzing(true);
    try {
      const verification = ver && ver.status === "verified"
        ? { status: "verified", input_address: ver.input_address, matched_address: ver.matched_address, latitude: ver.latitude, longitude: ver.longitude, provider: ver.provider, match_quality: ver.match_quality, imagery: imagery || null, imagery_available: imgOk, parcel: parcel ? { parcel_id: parcel.parcel_id, land_sqft: parcel.land_sqft, use_code: parcel.use_code, source: parcel.source } : { found: false } }
        : { status: "unverified_override", input_address: f.address, provider: ver?.provider || "US Census Geocoder" };
      const body = { ...f, verification, property_confirmed: confirmed, photo_count: photoId ? 1 : 0, property_photo_id: photoId || "", parcel_land_sqft: parcel?.land_sqft || null };
      const r = await api.post("/admin/ai-quote/analyze", body);
      setResult(r.data);
      setOv({ price_recommended: r.data.pricing.price_recommended, description: f.services.join(", ") });
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "AI analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const createQuote = () => run(async () => {
    const a = result.analysis, p = result.pricing;
    const line_items = p.line_items.map((li) => ({ description: li.description, qty: 1, rate: li.rate }));
    const price = parseFloat(ov.price_recommended) || p.price_recommended;
    const liSum = line_items.reduce((s, li) => s + Number(li.rate), 0);
    if (line_items.length && Math.abs(liSum - price) > 0.5) line_items.push({ description: "Adjustment", qty: 1, rate: round2(price - liSum) });
    else if (!line_items.length) line_items.push({ description: f.services.join(", ") || "Service", qty: 1, rate: price });
    const verifiedAddr = ver?.status === "verified" ? ver.matched_address : f.address;
    const ai_analysis = {
      property: a.property, verified_facts: a.verified_facts, estimates: a.estimates, job: a.job,
      assumptions: a.assumptions, warnings: a.warnings, confidence: a.confidence, confidence_reason: a.confidence_reason,
      pricing: p, operator_overrides: ov, final_price: price, verification: a.verification || null,
      property_confirmed: confirmed, property_photo_id: photoId || "",
      latitude: lat, longitude: lon, parcel: parcel || null, imagery: imagery || null,
      generated_at: new Date().toISOString(),
    };
    const r = await api.post("/admin/quotes", {
      customer_id: f.customer_id, property: verifiedAddr, latitude: lat, longitude: lon, services: f.services,
      line_items, notes: ov.description || "", internal_notes: `AI Quote · confidence ${a.confidence}. ${a.confidence_reason || ""}`,
      status: "Draft", ai_analysis,
    });
    onCreated?.(r.data); onClose();
  }, "Quote created from AI analysis");

  const a = result?.analysis, p = result?.pricing;
  const dataConfidence = ver?.status === "verified" && confirmed ? (parcel && imagery?.quality === "HIGH" ? "HIGH" : "MEDIUM") : "LOW";

  const AerialImage = ({ big }) => (
    <div className={`relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 ${big ? "h-[70vh]" : "h-[300px]"}`} data-testid="ai-aerial-preview">
      {bbox && imgOk ? (
        <>
          <img src={naipUrl(bbox)} alt="Property aerial" className="w-full h-full object-cover" onError={() => setImgOk(false)} />
          {parcel?.ring && (viewMode === "parcel" || viewMode === "property") && (
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
              <polygon points={ringPct()} fill="rgba(214,40,40,0.12)" stroke="#D62828" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"><Crosshair className="w-5 h-5 text-white drop-shadow" /></div>
          <div className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-2 py-1 flex items-center justify-between">
            <span>Imagery: USGS / USDA NAIP{imagery?.year ? ` · ${imagery.year}` : ""}{imagery?.resolution_value ? ` · ${imagery.resolution_value} ${(imagery.resolution_units || "m").toLowerCase()}` : ""}</span>
            <span className="opacity-80 hidden sm:inline">May not reflect recent changes</span>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-amber-800 bg-amber-50 p-3 text-center" data-testid="ai-imagery-fallback">
          High-resolution imagery unavailable. Confirm the address below or upload a current property photo.
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="ai-quote-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase flex items-center gap-2"><Sparkles className="w-5 h-5 text-[var(--action)]" /> Ghost AI Quote Maker</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">Powered by <a href="https://prestigiouscompanies.org" target="_blank" rel="noopener noreferrer" className="text-[var(--action)] hover:underline font-semibold">Prestigious Companies</a></p>
          </div>
          <button onClick={onClose} data-testid="ai-quote-close"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Property / Service Address</label>
              <div className="flex gap-2">
                <input className="flex-1 px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 ring-[var(--action)]"
                  value={f.address} onChange={(e) => onAddressChange(e.target.value)} placeholder="123 Main Street, Orlando, FL 32801" data-testid="ai-address" />
                <Btn onClick={verifyAddress} loading={verifying} disabled={verifying} data-testid="ai-verify-address">
                  {ver?.status === "verified" ? <><ShieldCheck className="w-4 h-4" /> Verified</> : <><MapPin className="w-4 h-4" /> Verify Address</>}
                </Btn>
              </div>
            </div>

            {ver && (
              <div className="rounded-xl border border-gray-200 p-3 space-y-3" data-testid="ai-verify-panel">
                {ver.status === "verified" && (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[11px] uppercase text-gray-400 font-semibold">Matched Property</p>
                        <p className="font-semibold text-[var(--navy)]" data-testid="ai-matched-address">{ver.matched_address}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{lat?.toFixed(5)}, {lon?.toFixed(5)} · {ver.provider}</p>
                      </div>
                      <Badge color="green"><ShieldCheck className="w-3 h-3 inline mr-1" />Verified</Badge>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <Btn variant={viewMode === "property" ? "blue" : "ghost"} className="!py-1 !px-2.5 !text-xs" onClick={() => setViewMode("property")} data-testid="ai-view-property">Property View</Btn>
                      <Btn variant={viewMode === "parcel" ? "blue" : "ghost"} className="!py-1 !px-2.5 !text-xs" onClick={() => setViewMode("parcel")} disabled={!parcel} data-testid="ai-view-parcel">Parcel View</Btn>
                      <Btn variant={viewMode === "wider" ? "blue" : "ghost"} className="!py-1 !px-2.5 !text-xs" onClick={() => setViewMode("wider")} data-testid="ai-view-wider">Wider View</Btn>
                      <Btn variant="ghost" className="!py-1 !px-2.5 !text-xs" onClick={() => setEnlarge(true)} data-testid="ai-view-larger"><Maximize2 className="w-3.5 h-3.5" /> View Larger</Btn>
                    </div>

                    {intelLoading ? (
                      <div className="h-[300px] rounded-lg border border-gray-200 flex items-center justify-center text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading best available property imagery…</div>
                    ) : <AerialImage />}

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
                      <span>Parcel: {parcel ? <b className="text-green-700">Identified ✓</b> : (intel?.parcel?.skipped ? "n/a (outside FL)" : "unavailable")}</span>
                      {parcel?.land_sqft ? <span>Lot: <b>{Number(parcel.land_sqft).toLocaleString()} sq ft</b></span> : null}
                      {imagery?.quality ? <span>Imagery quality: <b>{imagery.quality}</b></span> : null}
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                      {!confirmed ? (
                        <>
                          <Btn variant="blue" onClick={() => setConfirmed(true)} data-testid="ai-confirm-property"><CheckCircle2 className="w-4 h-4" /> Yes, This Is the Property</Btn>
                          <Btn variant="ghost" onClick={resetVerification} data-testid="ai-wrong-property">Wrong Property</Btn>
                        </>
                      ) : (
                        <span className="flex items-center gap-2 text-green-700 text-sm font-semibold" data-testid="ai-property-confirmed"><CheckCircle2 className="w-4 h-4" /> Property confirmed</span>
                      )}
                      <NavigateToProperty address={ver.matched_address} latitude={lat} longitude={lon} verified confirmed={confirmed} compact />
                    </div>
                  </>
                )}

                {ver.status === "multiple_matches" && (
                  <div data-testid="ai-multiple-matches">
                    <p className="text-sm font-semibold text-[var(--navy)] mb-1">We found multiple possible properties:</p>
                    <div className="space-y-1.5">
                      {ver.matches.map((m, i) => (
                        <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="radio" name="addr-match" checked={chosenIdx === i} onChange={() => setChosenIdx(i)} className="accent-[var(--action)]" data-testid={`ai-match-${i}`} /> {m.matched_address}
                        </label>
                      ))}
                    </div>
                    <Btn className="mt-2" onClick={useMatch} data-testid="ai-use-selected">Use Selected Address</Btn>
                  </div>
                )}

                {(ver.status === "not_found" || ver.status === "error") && (
                  <div data-testid="ai-not-verified">
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Address Not Verified</p>
                    <p className="text-xs text-gray-500 mt-0.5">{ver.status === "error" ? "The verification service is temporarily unavailable." : "We couldn't confidently verify this address."}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Btn variant="ghost" onClick={verifyAddress} loading={verifying}>Try Again</Btn>
                      <Btn variant="danger" onClick={() => { setOverride(true); toast.warning("Continuing unverified — confidence reduced."); }} data-testid="ai-override">Continue With Unverified Address</Btn>
                    </div>
                    {override && <p className="text-xs text-red-600 mt-2 font-semibold">Address Verification: UNVERIFIED — OPERATOR OVERRIDE</p>}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <ASelect label="Property Type" value={f.property_type} options={["residential", "commercial"]} onChange={(e) => setF({ ...f, property_type: e.target.value })} data-testid="ai-ptype" />
              <ASelect label="Customer (optional)" value={f.customer_id} options={[{ value: "", label: "— None —" }, ...customers.map((c) => ({ value: c.id, label: `${c.name} (${c.customer_id})` }))]} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="ai-customer" />
            </div>
            <div>
              <label className={labelCls}>Services</label>
              <div className="flex flex-wrap gap-1.5">
                {SERVICES.map((s) => (
                  <button key={s} type="button" onClick={() => toggleSvc(s)} data-testid={`ai-svc-${s.replace(/[^a-z]/gi, "")}`}
                    className={`text-xs px-2.5 py-1.5 rounded-full border ${f.services.includes(s) ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white text-gray-600 border-gray-200"}`}>{s}</button>
                ))}
              </div>
            </div>
            <ATextarea label="Job Description / Instructions (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="ai-desc" />
            <ATextarea label="Access / Surface Conditions (optional)" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} rows={2} />
            <MediaPicker label="Current Property Photo (optional)" value={photoId} onChange={setPhotoId} allowCapture allowExternalUrl previewClass="h-36" testid="ai-property-photo"
              hint="Best source for current surface condition — algae, stains, cracks — especially when aerial imagery is old." />

            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs text-gray-400">{canAnalyze ? "Ready to analyze." : "Verify & confirm the property to enable analysis."}</span>
              <div className="flex gap-2">
                <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                <Btn onClick={analyze} loading={analyzing} disabled={!canAnalyze} data-testid="ai-analyze-btn"><Sparkles className="w-4 h-4" /> Analyze & Estimate</Btn>
              </div>
            </div>
          </div>
        )}

        {step === 2 && result && (
          <div className="space-y-4" data-testid="ai-quote-result">
            <div className="rounded-lg border border-gray-200 p-3 text-sm" data-testid="ai-result-verification">
              <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Property Verification</p>
              <p>Address: <b>{ver?.status === "verified" ? ver.matched_address : f.address}</b></p>
              <p className="flex flex-wrap items-center gap-2 mt-0.5">
                Verification: {ver?.status === "verified" ? <Badge color="green"><ShieldCheck className="w-3 h-3 inline mr-1" />Verified</Badge> : <Badge color="amber"><ShieldAlert className="w-3 h-3 inline mr-1" />Unverified</Badge>}
                <span>Confirmed: <b>{confirmed ? "Yes" : "No"}</b></span>
              </p>
              {lat != null && <p className="text-gray-500">Location: {lat?.toFixed(5)}, {lon?.toFixed(5)} · {ver?.provider}</p>}
              <p className="text-gray-500">Imagery: {imagery?.provider || "n/a"}{imagery?.year ? ` · ${imagery.year}` : ""} · Parcel: {parcel ? "Florida parcel data ✓" : "n/a"} · Data Confidence: <b>{dataConfidence}</b></p>
              {lat != null && <div className="mt-2"><NavigateToProperty address={ver?.status === "verified" ? ver.matched_address : f.address} latitude={lat} longitude={lon} verified={ver?.status === "verified"} confirmed={confirmed} /></div>}
            </div>

            <div className="flex items-center gap-2">
              <Badge color={confColor(a.confidence)}>Confidence: {a.confidence}</Badge>
              <span className="text-xs text-gray-500">{a.confidence_reason}</span>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Property</p>
                <p>Type: <b>{a.property?.type || f.property_type}</b> · Stories: <b>{a.property?.stories || "?"}</b></p>
                {parcel?.land_sqft ? <p className="text-gray-500">Parcel lot: {Number(parcel.land_sqft).toLocaleString()} sq ft <span className="text-[10px]">(FL parcel data)</span></p> : null}
                <p className="text-xs uppercase text-gray-400 font-semibold mt-2 mb-1">Estimated Service Area</p>
                {a.estimates?.map((e, i) => <p key={i}>{e.service}: <b>~{Math.round(e.area)} {e.unit}</b></p>)}
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Job & Cost (internal)</p>
                <p>Crew: {a.job?.crew_size} · Elapsed: {a.job?.elapsed_hours}h · Labor-hrs: {p.labor_hours}</p>
                <p>Labor: ${p.labor_cost} · Materials: ${p.material_allowance} · Equip: ${p.equipment_allowance}</p>
                <p>Internal cost: <b>${p.internal_cost}</b></p>
                <p>Est. gross profit: <b>${p.gross_profit}</b> ({p.gross_margin}%)</p>
              </div>
            </div>

            <div className="bg-[var(--navy)] text-white rounded-lg p-4">
              <p className="text-xs uppercase text-white/60">Recommended Price (Range ${p.price_low} – ${p.price_high})</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg">$</span>
                <input type="number" step="0.01" value={ov.price_recommended} onChange={(e) => setOv({ ...ov, price_recommended: e.target.value })}
                  className="bg-white/10 border border-white/20 rounded px-2 py-1 text-2xl font-extrabold w-40" data-testid="ai-price-override" />
                <Badge color="blue">Editable</Badge>
              </div>
            </div>

            {(a.assumptions?.length || a.warnings?.length) ? (
              <div className="text-xs space-y-2">
                {a.assumptions?.length ? <div><p className="font-semibold text-gray-600">Assumptions</p><ul className="list-disc pl-4 text-gray-500">{a.assumptions.map((x, i) => <li key={i}>{x}</li>)}</ul></div> : null}
                {a.warnings?.length ? <div className="bg-amber-50 border border-amber-200 rounded p-2"><p className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Verify in field</p><ul className="list-disc pl-4 text-amber-700">{a.warnings.map((x, i) => <li key={i}>{x}</li>)}</ul></div> : null}
              </div>
            ) : null}

            <ATextarea label="Customer-facing description" value={ov.description} onChange={(e) => setOv({ ...ov, description: e.target.value })} rows={2} data-testid="ai-final-desc" />
            <p className="text-[11px] text-gray-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Internal cost, margin, coordinates, parcel and AI notes are saved with the quote but never shown to the customer.</p>

            <div className="flex justify-between gap-2">
              <Btn variant="ghost" onClick={() => setStep(1)}>← Back</Btn>
              <div className="flex gap-2">
                <Btn variant="ghost" onClick={analyze} loading={analyzing}>Re-analyze</Btn>
                <Btn onClick={createQuote} loading={saving} data-testid="ai-create-quote"><CheckCircle2 className="w-4 h-4" /> Create Quote</Btn>
              </div>
            </div>
          </div>
        )}
      </div>

      {enlarge && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="ai-enlarge">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEnlarge(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-4xl p-3">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-sm font-semibold text-[var(--navy)]">{ver?.matched_address}</span>
              <button onClick={() => setEnlarge(false)}><X className="w-5 h-5" /></button>
            </div>
            <AerialImage big />
          </div>
        </div>
      )}
    </div>
  );
}
